import React from "react"
import { useTranslation } from "react-i18next"
import { Box, Typography, styled } from "@mui/material"

import { isFreshFinance } from "helpers/features"
import Bar from "components/ui/Bar"
import Badge from "components/Debt/Badge"
import { DebtType } from "types"
import { colors } from "theme"

type Props = React.HTMLAttributes<HTMLDivElement> & {
  debt: DebtType
  compact?: boolean
}

const TitleComponent = ({ debt, compact }: Props) => {
  const { t } = useTranslation()

  const {
    customer,
    reference_number,
    phase,
    total_paid_percent,
    overdue_percent,
    next_payment_percent,
  } = debt

  return (
    <>
      {compact ? (
        <CompactContainer>
          <CompactContent>
            <Title variant="h3">{reference_number}</Title>
            <Progress>
              <Bar
                variant="determinate"
                paid={total_paid_percent}
                overdue={overdue_percent}
                nextPayment={isFreshFinance() ? next_payment_percent : 0}
              />
            </Progress>
          </CompactContent>
          {isFreshFinance() && (
            <CompactBadgeContent>
              <Badge phase={phase} />
            </CompactBadgeContent>
          )}
        </CompactContainer>
      ) : (
        <Container>
          {customer && <Customer variant="h4">{customer.name}</Customer>}
          <Title
            variant="h6"
            dangerouslySetInnerHTML={{
              __html: t("features.debt.title", { reference_number }),
            }}
          />
          {isFreshFinance() && <Badge phase={phase} />}
        </Container>
      )}
    </>
  )
}

TitleComponent.defaultProps = {
  compact: false,
}

export default TitleComponent

const Container = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
}))

const CompactContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    justifyContent: "flex-start",
  },
}))

const CompactContent = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "stretch",
}))

const CompactBadgeContent = styled(Box)(() => ({
  margin: "0 0 0 7px",
}))

const Customer = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    fontSize: "1.125rem",
    marginBottom: "0",
  },
}))

const Title = styled(Typography)(({ theme }) => ({
  margin: "5px 0 7px 0",
  [theme.breakpoints.down("md")]: {
    fontSize: "1.125rem",
    marginBottom: "0",
  },
}))

const Progress = styled(Box)(({ theme }) => ({
  background: colors.brightGray,
  borderRadius: "5px",
  height: "10px",
  margin: "5px 0 0 0",
  overflow: "hidden",
  position: "relative",
  [theme.breakpoints.up("md")]: {},
}))
